import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  LocalModal,
  ModalContentContainer,
  TableSearchBox,
} from '@/components';
import {
  MemberFilters,
  MemberSortColumns,
  MemberStates,
  urls,
} from '@/constants';
import {
  selectUserProfileResult,
  useFetchOrganizationMembersInfiniteQuery,
  useFetchOrganizationSettingsQuery,
} from '@/store';
import {
  MemberFilterKey,
  MemberSortColumn,
  MemberSortDirection,
  OrganizationMember,
} from '@/types';

import {
  BulkEdit,
  CsvDownload,
  EditMemberForm,
  InviteMembers,
  InviteMembersButton,
  MemberRow,
  MemberStateFilters,
  SkeletonTable,
} from './components';
import { searchMembers, sortMembersFactory } from './utils';

const ROW_BATCH_SIZE = 5000;

export const MemberManagement = () => {
  const { isLoading: isProfileLoading, data: profileData } = useSelector(
    selectUserProfileResult,
  );

  const {
    data: membersData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchOrganizationMembersInfiniteQuery({
    limit: ROW_BATCH_SIZE,
  });
  const members = useMemo<OrganizationMember[]>(
    () =>
      membersData?.pages.flatMap((page: OrganizationMember[]) => page) ?? [],
    [membersData],
  );
  const memberId = useMemo(
    () =>
      members.find(m => m.userEmail === profileData?.email)
        ?.organizationMemberId ?? null,
    [members, profileData],
  );
  const activeMemberEmails = useMemo(
    () =>
      members
        ?.filter(
          m =>
            m.state !== MemberStates.ARCHIVED &&
            m.state !== MemberStates.USER_DELETED,
        )
        .map(m => m.organizationEmail),
    [members],
  );

  const { data: organizationSettings } = useFetchOrganizationSettingsQuery();

  const [activeFilter, setActiveFilter] = useState<MemberFilterKey>(
    MemberFilters.ALL_NON_ARCHIVED.key,
  );
  const [searchPhrase, setSearchPhrase] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<MemberSortColumn>('name');
  const [sortDirection, setSortDirection] =
    useState<MemberSortDirection>('asc');
  const [selectedOrganizationMemberIds, setSelectedOrganizationMemberIds] =
    useState<number[]>([]);

  const isAddEditAvailable =
    organizationSettings?.integrationsConfig?.memberAdmin?.active &&
    organizationSettings.integrationsConfig.memberAdmin.hasDefaultPlan &&
    organizationSettings.isManagedOrg === false;
  const showEditButton =
    isAddEditAvailable && activeFilter !== MemberFilters.REMOVED.key;

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const closeInviteModal = () => {
    setIsInviteModalOpen(false);
  };
  const [selectedIdToEdit, setSelectedIdToEdit] = useState<number | null>(null);
  const closeEditModal = () => {
    setSelectedIdToEdit(null);
  };

  const memberToEdit = useMemo(
    () => members?.find(m => m.organizationMemberId === selectedIdToEdit),
    [members, selectedIdToEdit],
  );

  const numSelectedOrganizationMembers = selectedOrganizationMemberIds.length;

  const filteredData = useMemo(() => {
    if (!members) return [];
    const stateFilteredData = members.filter(m =>
      MemberFilters[activeFilter].states.includes(m.state),
    );
    const searchFilteredData = searchMembers(searchPhrase, stateFilteredData);
    return [...searchFilteredData].sort(
      sortMembersFactory(sortColumn, sortDirection),
    );
  }, [members, searchPhrase, activeFilter, sortColumn, sortDirection]);

  const isAllSelected =
    numSelectedOrganizationMembers > 0 &&
    numSelectedOrganizationMembers === filteredData.length;

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const toggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOrganizationMemberIds(
      event.target.checked
        ? filteredData.map(item => item.organizationMemberId)
        : [],
    );
  };

  const toggleSelectMember = useCallback((organizationMemberId: number) => {
    setSelectedOrganizationMemberIds(prevState =>
      prevState.includes(organizationMemberId)
        ? prevState.filter(id => id !== organizationMemberId)
        : [...prevState, organizationMemberId],
    );
  }, []);

  useEffect(() => {
    setSelectedOrganizationMemberIds([]);
  }, [members]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isSelfSelected = useMemo(
    () =>
      memberId === selectedIdToEdit ||
      selectedOrganizationMemberIds.includes(Number(memberId)),
    [selectedOrganizationMemberIds, memberId, selectedIdToEdit],
  );

  if (
    !isProfileLoading &&
    !profileData?.memberRoles?.some(r => r.roleId === 'member_admin')
  ) {
    return (
      <Box minHeight="60vh">
        <Typography variant="h1" gutterBottom color="red">
          No Access
        </Typography>
        <Typography variant="body2">
          Please contact{' '}
          <a href={`mailto:${urls.supportEmail}`}>LumiQ support</a> if you
          should have access
        </Typography>
      </Box>
    );
  }

  const handleSort = (column: MemberSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  return (
    <Box>
      <Box sx={styles.toolbar}>
        <Box sx={styles.toolbarLeft}>
          <Typography variant="h1">Members</Typography>
          {!isMobile && (
            <TableSearchBox
              placeHolder="Search Members"
              onSearch={search => {
                setSearchPhrase(search);
              }}
            />
          )}
        </Box>
        <Box sx={styles.toolbarRight}>
          {isAddEditAvailable && (
            <InviteMembersButton
              onClick={() => setIsInviteModalOpen(true)}
              activeMemberEmails={activeMemberEmails}
            />
          )}
          <CsvDownload data={members} />
        </Box>
      </Box>
      <Box sx={styles.searchContainer}>
        <MemberStateFilters
          activeFilter={activeFilter}
          onFilterSelection={filter => {
            setSelectedOrganizationMemberIds([]);
            setActiveFilter(filter);
          }}
          data={members}
        />
        {isMobile && (
          <TableSearchBox
            placeHolder="Search Members"
            onSearch={search => {
              setSearchPhrase(search);
            }}
          />
        )}
      </Box>
      <Box sx={styles.table} id="table">
        <TableContainer>
          <Table
            sx={{
              minWidth: 800,
              backgroundColor: 'surface.base',
              mt: 4,
              borderRadius: 2,
            }}
            aria-label="Organization Members">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    disabled={
                      isLoading || activeFilter === MemberFilters.REMOVED.key
                    }
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    slotProps={{
                      input: { 'aria-label': 'Select all members' },
                    }}
                  />
                </TableCell>
                {MemberSortColumns.map(c => (
                  <TableCell
                    key={c.name}
                    width={c?.width}
                    sx={{ paddingBlock: 2 }}>
                    <TableSortLabel
                      active={sortColumn === c.sortKey}
                      direction={sortDirection}
                      onClick={() => handleSort(c.sortKey)}>
                      <Typography variant="body1SemiBold">{c.name}</Typography>
                    </TableSortLabel>
                  </TableCell>
                ))}
                {showEditButton && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* TODO: empty state */}
              {isLoading ? (
                <SkeletonTable rows={5} />
              ) : (
                filteredData.map(row => (
                  <MemberRow
                    key={row.organizationMemberId}
                    organizationMemberId={row.organizationMemberId}
                    toggleSelectMember={toggleSelectMember}
                    isDisabled={activeFilter === MemberFilters.REMOVED.key}
                    isSelected={
                      isAllSelected ||
                      selectedOrganizationMemberIds.includes(
                        row.organizationMemberId,
                      )
                    }
                    isManagedOrg={!!organizationSettings?.isManagedOrg}
                    name={row.name}
                    organizationEmail={row.organizationEmail}
                    userEmail={row.userEmail}
                    state={row.state}
                    lastActivityDate={row.lastActivityDate}
                    lastActivityType={row.lastActivityType}
                    organizationMemberCreatedAt={
                      row.organizationMemberCreatedAt
                    }
                    openEditModal={
                      showEditButton ? setSelectedIdToEdit : undefined
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <BulkEdit
        members={members ?? []}
        setSelectedOrganizationMemberIds={setSelectedOrganizationMemberIds}
        selectedOrganizationMemberIds={selectedOrganizationMemberIds}
        isManagedOrg={!!organizationSettings?.isManagedOrg}
        isSelfSelected={isSelfSelected}
      />
      <LocalModal
        open={!!memberToEdit}
        name="MEMBER_EDIT"
        fullWidth={!isDesktop}>
        <ModalContentContainer onModalClose={closeEditModal} stretch>
          {memberToEdit && (
            <EditMemberForm
              member={memberToEdit}
              goBack={closeEditModal}
              isArchivingDisabled={
                !!organizationSettings?.isManagedOrg || isSelfSelected
              }
            />
          )}
        </ModalContentContainer>
      </LocalModal>
      <LocalModal
        open={isInviteModalOpen}
        name="MEMBER_INVITE"
        fullWidth={!isDesktop}>
        <ModalContentContainer onModalClose={closeInviteModal} stretch>
          <InviteMembers
            onSuccess={closeInviteModal}
            activeMemberEmails={activeMemberEmails ?? []}
          />
        </ModalContentContainer>
      </LocalModal>
    </Box>
  );
};

const styles = {
  toolbar: () => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: { xs: 'wrap' },
    gap: 1,
    mb: 1,
  }),
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  },
  table: {
    mb: 4,
  },
};
